import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const WishlistContext = createContext(null);
const STORAGE_KEY = "elora_wishlist";

// Only non-sensitive catalogue snapshots (public product data) are kept in localStorage for guests.
function loadLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (err) {
    logger.warn("Could not read saved wishlist", err);
    return [];
  }
}

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(loadLocal); // array of product objects
  const syncedFor = useRef(null);
  const userId = user?.id || null;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // On login: merge local wishlist into the server, then load the server copy.
  useEffect(() => {
    if (!userId || syncedFor.current === userId) return;
    syncedFor.current = userId;
    (async () => {
      try {
        for (const p of loadLocal()) {
          await api.post("/wishlist", { product_id: p.handle }).catch(() => {});
        }
        const { data } = await api.get("/wishlist");
        setItems(data.items || []);
      } catch (err) {
        logger.warn("Wishlist sync failed", err);
      }
    })();
  }, [userId]);

  const has = useCallback((handle) => items.some((p) => p.handle === handle), [items]);

  const toggle = useCallback(
    async (product) => {
      if (has(product.handle)) {
        setItems((prev) => prev.filter((p) => p.handle !== product.handle));
        if (userId) await api.delete(`/wishlist/${product.handle}`).catch(() => {});
        toast("Removed from wishlist", { description: product.title });
      } else {
        setItems((prev) => [...prev, product]);
        if (userId) await api.post("/wishlist", { product_id: product.handle }).catch(() => {});
        toast("Added to wishlist", { description: product.title });
      }
    },
    [has, userId]
  );

  const remove = useCallback(
    async (handle) => {
      setItems((prev) => prev.filter((p) => p.handle !== handle));
      if (userId) await api.delete(`/wishlist/${handle}`).catch(() => {});
    },
    [userId]
  );

  const value = useMemo(() => ({ items, count: items.length, has, toggle, remove }), [items, has, toggle, remove]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => useContext(WishlistContext);
